// src/pages/Admin/Enquiries.jsx
import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Menu,
  MenuItem,
  Avatar,
  Badge,
  Select,
  FormControl,
  InputLabel,
  Tooltip,
  Alert,
  LinearProgress,
  Tabs,
  Tab,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Stepper,
  Step,
  StepLabel,
  DialogContentText,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormLabel,
  CircularProgress,
} from '@mui/material';
import {
  Search,
  FilterList,
  Refresh,
  MoreVert,
  Visibility,
  Edit,
  Delete,
  Assignment,
  LocalShipping,
  AttachMoney,
  Person,
  Email,
  Phone,
  Schedule,
  CheckCircle,
  Cancel,
  Chat,
  Download,
  ArrowForward,
  Receipt,
  PictureAsPdf,
  Send,
  Add,
  Print,
  Share,
  Description,
  Payment,
  Upload,
  CloudUpload,
  AttachFile,
  Description as DescriptionIcon,
  FileCopy,
  Close,
  Delete as DeleteIcon,
  NoteAdd,
  Inventory,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { adminAPI } from '../../services/api';
import { formatCurrency, formatDate, getStatusColor } from '../../utils/helpers';
import toast from 'react-hot-toast';

const AdminEnquiries = () => {
  const navigate = useNavigate();
  const { id: urlId } = useParams();
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detailEnquiry, setDetailEnquiry] = useState(null);
  const [detailDialog, setDetailDialog] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [tabValue, setTabValue] = useState(0);
  const [statusDialog, setStatusDialog] = useState(null);
  const [invoiceDialog, setInvoiceDialog] = useState(null);
  const [uploadInvoiceDialog, setUploadInvoiceDialog] = useState(null);
  const [viewInvoiceDialog, setViewInvoiceDialog] = useState(null);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [editInvoiceMode, setEditInvoiceMode] = useState(false);
  const [editInvoiceData, setEditInvoiceData] = useState({});
  const [invoiceDetails, setInvoiceDetails] = useState({
    invoiceNumber: '',
    issueDate: new Date().toISOString().split('T')[0],
    dueDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    items: [],
    taxRate: 18,
    discount: 0,
    notes: '',
    paymentTerms: 'Net 7 days',
    invoiceFile: null,
    invoiceType: 'generate',
  });
  const [invoiceFile, setInvoiceFile] = useState(null);
  const [uploadUnitPrice, setUploadUnitPrice] = useState('');

  // ── PO / Seller Notes ──────────────────────────────────────────────────────
  const [poDialog, setPoDialog]   = useState(null);  // enquiry object
  const [poNotes, setPoNotes]     = useState('');
  const [poSaving, setPoSaving]   = useState(false);

  // ── Seller Assignment ───────────────────────────────────────────────────────
  const [sellerDialog,   setSellerDialog]   = useState(null); // enquiry object
  const [sellers,        setSellers]        = useState([]);
  const [selectedSeller, setSelectedSeller] = useState('');
  const [sellerAssigning,setSellerAssigning]= useState(false);

  // ── Payment Terms per Enquiry ────────────────────────────────────────────────
  const [ptDialog,   setPtDialog]   = useState(null); // enquiry object
  const [ptTerms,    setPtTerms]    = useState('full_100');
  const [ptSaving,   setPtSaving]   = useState(false);
  const PAYMENT_TERMS_OPTIONS = [
    { value: 'full_100',  label: 'Full Payment (100%)',      desc: '100% before shipment' },
    { value: '30_70',     label: 'Split: 30% + 70%',        desc: '30% advance + 70% before shipment' },
    { value: '40_30_30',  label: 'Split: 40% + 30% + 30%',  desc: '40% booking + 30% on dispatch + 30% on arrival' },
    { value: '25_51_24',  label: 'Split: 25% + 51% + 24%',  desc: '25% advance + 51% on shipment + 24% on delivery' },
  ];
  const handleOpenPtDialog = (enq) => {
    setPtTerms(enq.payment_terms || 'full_100');
    setPtDialog(enq);
  };
  const handleSavePtTerms = async () => {
    if (!ptDialog) return;
    const eid = ptDialog._id || ptDialog.id;
    setPtSaving(true);
    try {
      await adminAPI.setEnquiryPaymentTerms(eid, ptTerms);
      toast.success('Payment terms updated for this enquiry');
      setPtDialog(null);
      fetchEnquiries();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Failed to update payment terms');
    } finally {
      setPtSaving(false);
    }
  };

  useEffect(() => {
    fetchEnquiries();
  }, []);

  // Auto-open detail dialog when navigating to /admin/enquiries/:id
  useEffect(() => {
    if (urlId && enquiries.length > 0) {
      const found = enquiries.find(e => String(e._id || e.id) === String(urlId));
      if (found) { setDetailEnquiry(found); setDetailDialog(true); }
      else {
        // Fetch from API if not in list
        adminAPI.getEnquiryById(urlId).then(res => {
          setDetailEnquiry(res.data);
          setDetailDialog(true);
        }).catch(() => {});
      }
    }
  }, [urlId, enquiries]);

  const fetchEnquiries = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getAllEnquiries();
      const data = Array.isArray(response.data)
        ? response.data
        : response.data?.enquiries || [];
      setEnquiries(data);
    } catch (error) {
      console.error('Error fetching enquiries:', error);
      toast.error('Failed to load enquiries');
    } finally {
      setLoading(false);
    }
  };

  // Tab index → status value
  const TAB_STATUS_MAP = ['all', 'pending', 'processing', 'invoice_sent', 'confirmed', 'cancelled'];

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setStatusFilter(TAB_STATUS_MAP[newValue]);
  };

  const handleStatusUpdate = async (id, status) => {
    try {
      await adminAPI.updateEnquiryStatus(id, { status });
      toast.success(`Enquiry status updated to ${status}`);
      fetchEnquiries();
      setStatusDialog(null);
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update status');
    }
  };

  const handleCreateInvoice = (enquiry) => {
    const invoiceNumber = `INV-${Date.now().toString().slice(-6)}`;
    const qty       = enquiry.quantity || 1;
    const unitPrice = enquiry.unitPrice || (enquiry.totalAmount ? enquiry.totalAmount / qty : 0);
    const items = [
      {
        description: enquiry.product || enquiry.productName || 'Fruit Purchase',
        quantity:    qty,
        unitPrice:   unitPrice,
        amount:      unitPrice * qty,
        hsn:         '',
        grade:       enquiry.selected_grade || '',
      },
    ];
    setInvoiceDetails((prev) => ({ ...prev, invoiceNumber, items }));
    setInvoiceDialog(enquiry);
  };

  const handleOpenUploadInvoice = (enquiry) => {
    setUploadInvoiceDialog(enquiry);
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    if (file.type !== 'application/pdf') {
      toast.error('Please upload a PDF file');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size should be less than 5MB');
      return;
    }
    setInvoiceFile(file);
    toast.success('File selected: ' + file.name);
  };

  const handleUploadInvoice = async () => {
    if (!uploadInvoiceDialog || !invoiceFile) {
      toast.error('Please select a PDF file to upload');
      return;
    }
    try {
      const qty   = uploadInvoiceDialog.quantity || 1;
      const unit  = parseFloat(uploadUnitPrice) || 0;
      const total = unit * qty;
      const formData   = new FormData();
      formData.append('invoiceFile', invoiceFile);
      formData.append('enquiryId', uploadInvoiceDialog._id || uploadInvoiceDialog.id);
      formData.append('buyerId', uploadInvoiceDialog.buyerId || uploadInvoiceDialog.user_id);
      formData.append('invoiceNumber', `INV-UPLOAD-${Date.now().toString().slice(-6)}`);
      formData.append('issueDate', new Date().toISOString().split('T')[0]);
      formData.append('totalAmount', total);
      formData.append('unitPrice',   unit);
      formData.append(
        'dueDate',
        new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
      );

      await adminAPI.uploadInvoice(formData);
      await adminAPI.updateEnquiryStatus(
        uploadInvoiceDialog._id || uploadInvoiceDialog.id,
        { status: 'invoice_sent' }
      );

      toast.success('Invoice uploaded successfully');
      fetchEnquiries();
      setUploadInvoiceDialog(null);
      setInvoiceFile(null);
      setUploadUnitPrice('');
    } catch (error) {
      console.error('Error uploading invoice:', error);
      toast.error('Failed to upload invoice');
    }
  };

  const calculateTotalAmount = () => {
    const subtotal = invoiceDetails.items.reduce((sum, item) => sum + item.amount, 0);
    const tax = (subtotal * invoiceDetails.taxRate) / 100;
    const discountAmt = (subtotal * invoiceDetails.discount) / 100;
    return subtotal + tax - discountAmt;
  };

  const handleSendInvoice = async () => {
    if (!invoiceDialog) return;
    const total = calculateTotalAmount();
    if (!total || total <= 0) {
      toast.error('Please enter a unit price before sending the invoice.');
      return;
    }
    try {
      const firstItem = invoiceDetails.items[0] || {};
      const invoiceData = {
        enquiryId:    invoiceDialog._id || invoiceDialog.id,
        invoiceNumber: invoiceDetails.invoiceNumber,
        issueDate:    invoiceDetails.issueDate,
        dueDate:      invoiceDetails.dueDate,
        items:        invoiceDetails.items,
        subtotal:     invoiceDetails.items.reduce((sum, item) => sum + item.amount, 0),
        taxRate:      invoiceDetails.taxRate,
        discount:     invoiceDetails.discount,
        totalAmount:  total,
        unitPrice:    firstItem.unitPrice || 0,
        quantity:     firstItem.quantity  || invoiceDialog.quantity || 1,
        hsn_code:     firstItem.hsn       || '',
        grade:        firstItem.grade     || '',
        notes:        invoiceDetails.notes,
        paymentTerms: invoiceDetails.paymentTerms,
        buyerId:      invoiceDialog.buyerId || invoiceDialog.user_id,
      };

      await adminAPI.createInvoice(invoiceData);
      await adminAPI.updateEnquiryStatus(
        invoiceDialog._id || invoiceDialog.id,
        { status: 'invoice_sent' }
      );

      toast.success('Invoice created and sent successfully');
      fetchEnquiries();
      setInvoiceDialog(null);
      resetInvoiceDetails();
    } catch (error) {
      console.error('Error creating invoice:', error);
      toast.error('Failed to create invoice');
    }
  };

  const handleViewInvoice = async (enquiry) => {
    try {
      const response = await adminAPI.getInvoiceByEnquiry(enquiry._id || enquiry.id);
      setSelectedInvoice(response.data);
      setViewInvoiceDialog(true);
    } catch (error) {
      console.error('Error fetching invoice:', error);
      toast.error('Failed to load invoice');
    }
  };

  const handleDownloadInvoice = async (invoiceId, invoiceNumber) => {
    try {
      const response = await adminAPI.downloadInvoice(invoiceId);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoice-${invoiceNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Invoice downloaded successfully');
    } catch (error) {
      console.error('Error downloading invoice:', error);
      toast.error('Failed to download invoice');
    }
  };

  const handleSendInvoiceToBuyer = async (invoiceId) => {
    try {
      await adminAPI.sendInvoiceToBuyer(invoiceId);
      toast.success('Invoice sent to buyer successfully');
    } catch (error) {
      console.error('Error sending invoice:', error);
      toast.error('Failed to send invoice');
    }
  };

  const handleEditInvoiceSave = async () => {
    try {
      const enquiryId = editInvoiceData.enquiryId || viewInvoiceDialog;
      // Update enquiry fields directly (no invoices table — data lives on enquiry)
      await adminAPI.updateEnquiryStatus(enquiryId, {
        status:        editInvoiceData.status || 'invoice_sent',
        total_amount:  parseFloat(editInvoiceData.totalAmount) || 0,
        unit_price:    parseFloat(editInvoiceData.unitPrice) || 0,
        invoice_number: editInvoiceData.invoiceNumber,
        due_date:      editInvoiceData.dueDate,
        payment_terms: editInvoiceData.paymentTerms,
        notes:         editInvoiceData.notes,
      });
      toast.success('Invoice updated successfully');
      setEditInvoiceMode(false);
      setViewInvoiceDialog(false);
      fetchEnquiries();
    } catch (err) {
      console.error('edit invoice error', err);
      toast.error('Failed to update invoice');
    }
  };

  const handleOpenPoDialog = (enquiry) => {
    setPoNotes(enquiry.seller_notes || '');
    setPoDialog(enquiry);
  };

  // ── Seller Assignment Handlers ──────────────────────────────────────────────
  const handleOpenSellerAssign = async (enquiry) => {
    setSelectedSeller(enquiry.assigned_seller_id ? String(enquiry.assigned_seller_id) : '');
    setSellerDialog(enquiry);
    if (sellers.length === 0) {
      try {
        const res = await adminAPI.getSellers();
        setSellers(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        toast.error('Failed to load sellers');
      }
    }
  };

  const handleAssignSeller = async () => {
    if (!sellerDialog) return;
    const eid = sellerDialog._id || sellerDialog.id;
    setSellerAssigning(true);
    try {
      await adminAPI.assignEnquirySeller(eid, selectedSeller ? parseInt(selectedSeller) : null);
      toast.success(selectedSeller ? 'Seller assigned successfully!' : 'Seller assignment removed.');
      fetchEnquiries();
      setSellerDialog(null);
    } catch (err) {
      toast.error('Failed to assign seller');
    } finally {
      setSellerAssigning(false);
    }
  };

  const handleSavePoNotes = async () => {
    if (!poDialog) return;
    const eid = poDialog._id || poDialog.id;
    setPoSaving(true);
    try {
      await adminAPI.setSellerNotes(eid, { seller_notes: poNotes });
      toast.success('PO instructions saved — supplier can now see them.');
      fetchEnquiries();
      setPoDialog(null);
    } catch (err) {
      console.error('Error saving seller notes:', err);
      toast.error('Failed to save PO instructions');
    } finally {
      setPoSaving(false);
    }
  };

  const resetInvoiceDetails = () => {
    setInvoiceDetails({
      invoiceNumber: '',
      issueDate: new Date().toISOString().split('T')[0],
      dueDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      items: [],
      taxRate: 18,
      discount: 0,
      notes: '',
      paymentTerms: 'Net 7 days',
      invoiceFile: null,
      invoiceType: 'generate',
    });
    setInvoiceFile(null);
  };

  // Stats computed from full enquiries list
  const stats = {
    total: enquiries.length,
    pending: enquiries.filter((e) => e.status === 'pending').length,
    processing: enquiries.filter((e) => e.status === 'processing').length,
    confirmed: enquiries.filter((e) => e.status === 'confirmed').length,
    invoice_sent: enquiries.filter((e) => e.status === 'invoice_sent').length,
    cancelled: enquiries.filter((e) => e.status === 'cancelled').length,
    revenue: enquiries
      .filter((e) => e.status === 'confirmed' || e.status === 'completed')
      .reduce((sum, e) => sum + (e.totalAmount || 0), 0),
  };

  // Client-side filter
  const filteredEnquiries = enquiries.filter((enquiry) => {
    const matchesSearch =
      (enquiry.buyerName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (enquiry.enquiryId || '').toString().toLowerCase().includes(searchTerm.toLowerCase()) ||
      (enquiry.product || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (enquiry.productName || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || enquiry.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <Container sx={{ py: 4, textAlign: 'center' }}>
        <LinearProgress sx={{ mb: 2 }} />
        <Typography>Loading enquiries...</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            Enquiries Management
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage and track all customer enquiries
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<Download />}
            onClick={() => toast.success('Export feature coming soon')}
          >
            Export
          </Button>
          <Button variant="contained" startIcon={<Refresh />} onClick={fetchEnquiries}>
            Refresh
          </Button>
        </Box>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {[
          { label: 'Total', value: stats.total, color: '#2196f3', icon: <Assignment /> },
          {
            label: 'Pending',
            value: stats.pending,
            color: '#ff9800',
            icon: <Schedule />,
            textColor: 'warning.main',
          },
          {
            label: 'Processing',
            value: stats.processing,
            color: '#00bcd4',
            icon: <LocalShipping />,
            textColor: 'info.main',
          },
          {
            label: 'Invoice Sent',
            value: stats.invoice_sent,
            color: '#9c27b0',
            icon: <Receipt />,
            textColor: 'secondary.main',
          },
          {
            label: 'Revenue',
            value: formatCurrency(stats.revenue),
            color: '#4caf50',
            icon: <AttachMoney />,
            textColor: 'success.main',
            isText: true,
          },
        ].map((stat, i) => (
          <Grid item xs={12} sm={6} md={2.4} key={i}>
            <Card sx={{ borderRadius: 2 }}>
              <CardContent>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <Box>
                    <Typography color="textSecondary" variant="body2">
                      {stat.label}
                    </Typography>
                    <Typography
                      variant="h4"
                      fontWeight="bold"
                      color={stat.textColor || 'text.primary'}
                      sx={stat.isText ? { fontSize: '1.4rem' } : {}}
                    >
                      {stat.value}
                    </Typography>
                  </Box>
                  <Avatar sx={{ bgcolor: `${stat.color}15`, color: stat.color }}>
                    {stat.icon}
                  </Avatar>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* ✅ FIXED: Removed duplicate iconPosition="start" on Invoice Sent tab */}
      <Paper sx={{ mb: 3, borderRadius: 2 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="All" icon={<Assignment />} iconPosition="start" />
          <Tab
            label="Pending"
            iconPosition="start"
            icon={
              <Badge badgeContent={stats.pending} color="warning">
                <Schedule />
              </Badge>
            }
          />
          <Tab label="Processing" icon={<LocalShipping />} iconPosition="start" />
          {/* ✅ FIX: Only one iconPosition="start" — removed the duplicate */}
          <Tab
            label="Invoice Sent"
            icon={
              <Badge badgeContent={stats.invoice_sent} color="secondary">
                <Receipt />
              </Badge>
            }
            iconPosition="start"
          />
          <Tab label="Confirmed" icon={<CheckCircle />} iconPosition="start" />
          <Tab label="Cancelled" icon={<Cancel />} iconPosition="start" />
        </Tabs>
      </Paper>

      {/* Search and Filters */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              placeholder="Search enquiries by buyer, ID, or product..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
              size="small"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Sort By</InputLabel>
                <Select label="Sort By" defaultValue="date_desc">
                  <MenuItem value="date_desc">Newest First</MenuItem>
                  <MenuItem value="date_asc">Oldest First</MenuItem>
                  <MenuItem value="amount_desc">Highest Amount</MenuItem>
                  <MenuItem value="amount_asc">Lowest Amount</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Enquiries Table */}
      <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <TableContainer>
          <Table>
            <TableHead sx={{ bgcolor: 'grey.50' }}>
              <TableRow>
                {[
                  'Enquiry ID',
                  'Buyer',
                  'Product',
                  'Specifications',
                  'Quantity',
                  'Amount',
                  'Status',
                  'Invoice',
                  'Date',
                  'Actions',
                ].map((h) => (
                  <TableCell key={h}>
                    <Typography variant="subtitle2" fontWeight="bold">
                      {h}
                    </Typography>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredEnquiries.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 6 }}>
                    <Assignment
                      sx={{
                        fontSize: 60,
                        color: 'text.secondary',
                        mb: 2,
                        display: 'block',
                        mx: 'auto',
                      }}
                    />
                    <Typography variant="h6" color="text.secondary">
                      No enquiries found
                    </Typography>
                    {statusFilter !== 'all' && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        No enquiries with status "{statusFilter}"
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              ) : (
                filteredEnquiries.map((enquiry) => {
                  const eid = enquiry._id || enquiry.id;
                  return (
                    <TableRow key={eid} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          #{enquiry.enquiryId || eid}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar sx={{ width: 36, height: 36 }}>
                            {(enquiry.buyerName || 'B').charAt(0).toUpperCase()}
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle2" fontWeight="medium">
                              {enquiry.buyerName || 'Unknown Buyer'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {enquiry.country || 'N/A'}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {enquiry.product || enquiry.productName || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" component="div" color="text.secondary">
                          {enquiry.selected_origin  && <span>Origin: {enquiry.selected_origin}<br /></span>}
                          {enquiry.selected_variety && <span>Variety: {enquiry.selected_variety}<br /></span>}
                          {enquiry.selected_grade   && <span>Grade: {enquiry.selected_grade}<br /></span>}
                          {enquiry.selected_size    && <span>Size: {enquiry.selected_size}<br /></span>}
                          {enquiry.port_of_discharge && <span>Port: {enquiry.port_of_discharge}</span>}
                          {!enquiry.selected_origin && !enquiry.selected_variety && !enquiry.selected_grade && !enquiry.selected_size && '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {(enquiry.quantity || 0).toLocaleString()} containers
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {formatCurrency(enquiry.totalAmount || 0)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={enquiry.status || 'pending'}
                          size="small"
                          color={getStatusColor(enquiry.status)}
                          sx={{ fontWeight: 'medium', textTransform: 'capitalize' }}
                        />
                      </TableCell>
                      <TableCell>
                        {enquiry.invoiceNumber ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Chip
                              icon={<Receipt />}
                              label={enquiry.invoiceNumber}
                              size="small"
                              color="secondary"
                              variant="outlined"
                              onClick={() => handleViewInvoice(enquiry)}
                              sx={{ cursor: 'pointer' }}
                            />
                          </Box>
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            No invoice
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(enquiry.createdAt || enquiry.created_at)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                          {/* View Details — always visible */}
                          <Tooltip title="View Details">
                            <IconButton
                              size="small"
                              onClick={() => navigate(`/admin/enquiries/${eid}`)}
                              color="primary"
                            >
                              <Visibility />
                            </IconButton>
                          </Tooltip>

                          {/* ✅ Create Invoice — always available unless cancelled */}
                          {enquiry.status !== 'cancelled' && (
                            <Tooltip title={enquiry.invoiceNumber ? 'Re-create Invoice' : 'Create Invoice'}>
                              <IconButton
                                size="small"
                                onClick={() => handleCreateInvoice(enquiry)}
                                color="secondary"
                              >
                                <Receipt />
                              </IconButton>
                            </Tooltip>
                          )}

                          {/* ✅ Upload Invoice PDF — always available unless cancelled */}
                          {enquiry.status !== 'cancelled' && (
                            <Tooltip title={enquiry.invoiceNumber ? 'Re-upload Invoice PDF' : 'Upload Invoice PDF'}>
                              <IconButton
                                size="small"
                                onClick={() => handleOpenUploadInvoice(enquiry)}
                                color="primary"
                              >
                                <Upload />
                              </IconButton>
                            </Tooltip>
                          )}

                          {/* ✅ View + Edit + Download + Resend when invoice exists */}
                          {enquiry.invoiceNumber && (
                            <>
                              <Tooltip title="View / Edit Invoice">
                                <IconButton
                                  size="small"
                                  onClick={() => handleViewInvoice(enquiry)}
                                  color="info"
                                >
                                  <Visibility />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Download Invoice PDF">
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    handleDownloadInvoice(
                                      enquiry.invoiceId,
                                      enquiry.invoiceNumber
                                    )
                                  }
                                  color="primary"
                                >
                                  <Download />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Send / Resend Invoice to Buyer">
                                <IconButton
                                  size="small"
                                  onClick={() => handleSendInvoiceToBuyer(enquiry.invoiceNumber || enquiry.invoice_number || enquiry.invoiceId)}
                                  color="success"
                                >
                                  <Send />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}

                          {/* Update Status — always visible */}
                          <Tooltip title="Update Status">
                            <IconButton
                              size="small"
                              onClick={() => setStatusDialog(enquiry)}
                              color="info"
                            >
                              <Edit />
                            </IconButton>
                          </Tooltip>

                          {/* PO / Supplier Notes */}
                          <Tooltip title={enquiry.seller_notes ? 'Edit Supplier PO Instructions' : 'Add Supplier PO Instructions'}>
                            <IconButton
                              size="small"
                              onClick={() => handleOpenPoDialog(enquiry)}
                              color={enquiry.seller_notes ? 'success' : 'default'}
                            >
                              <NoteAdd />
                            </IconButton>
                          </Tooltip>

                          {/* Assign Seller */}
                          <Tooltip title={enquiry.assigned_seller_id ? 'Change Assigned Seller' : 'Assign Seller'}>
                            <IconButton
                              size="small"
                              onClick={() => handleOpenSellerAssign(enquiry)}
                              color={enquiry.assigned_seller_id ? 'warning' : 'default'}
                            >
                              <Person />
                            </IconButton>
                          </Tooltip>

                          {/* Payment Terms per Enquiry */}
                          <Tooltip title={`Payment Terms: ${enquiry.payment_terms || 'full_100'} — Click to change`}>
                            <IconButton
                              size="small"
                              onClick={() => handleOpenPtDialog(enquiry)}
                              color={enquiry.payment_terms && enquiry.payment_terms !== 'full_100' ? 'secondary' : 'default'}
                            >
                              <Payment />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* ───────────────── Status Update Dialog ───────────────── */}
      <Dialog
        open={Boolean(statusDialog)}
        onClose={() => setStatusDialog(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Update Enquiry Status</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                label="Status"
                defaultValue={statusDialog?.status || 'pending'}
                onChange={(e) => {
                  if (statusDialog) {
                    handleStatusUpdate(
                      statusDialog._id || statusDialog.id,
                      e.target.value
                    );
                  }
                }}
              >
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="processing">Processing</MenuItem>
                <MenuItem value="confirmed">Confirmed</MenuItem>
                <MenuItem value="invoice_sent">Invoice Sent</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusDialog(null)}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* ───────────────── Create Invoice Dialog ───────────────── */}
      <Dialog
        open={Boolean(invoiceDialog)}
        onClose={() => {
          setInvoiceDialog(null);
          resetInvoiceDetails();
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Receipt />
            <Typography variant="h6">Create Invoice</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {invoiceDialog && (
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={3}>
                {/* Enquiry summary */}
                <Grid item xs={12}>
                  <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Enquiry Details
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="body2">
                          <strong>Buyer:</strong> {invoiceDialog.buyerName || 'Unknown'}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2">
                          <strong>Product:</strong>{' '}
                          {invoiceDialog.product || invoiceDialog.productName || 'N/A'}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2">
                          <strong>Quantity:</strong> {invoiceDialog.quantity || 0} containers
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2">
                          <strong>Amount:</strong>{' '}
                          {formatCurrency(invoiceDialog.totalAmount || 0)}
                        </Typography>
                      </Grid>
                      {invoiceDialog.selected_origin && (
                        <Grid item xs={6}>
                          <Typography variant="body2">
                            <strong>Origin Country:</strong> {invoiceDialog.selected_origin}
                          </Typography>
                        </Grid>
                      )}
                      {invoiceDialog.selected_variety && (
                        <Grid item xs={6}>
                          <Typography variant="body2">
                            <strong>Variety:</strong> {invoiceDialog.selected_variety}
                          </Typography>
                        </Grid>
                      )}
                      {invoiceDialog.selected_grade && (
                        <Grid item xs={6}>
                          <Typography variant="body2">
                            <strong>Grade:</strong> {invoiceDialog.selected_grade}
                          </Typography>
                        </Grid>
                      )}
                      {invoiceDialog.selected_size && (
                        <Grid item xs={6}>
                          <Typography variant="body2">
                            <strong>Size:</strong> {invoiceDialog.selected_size}
                          </Typography>
                        </Grid>
                      )}
                      {invoiceDialog.port_of_discharge && (
                        <Grid item xs={6}>
                          <Typography variant="body2">
                            <strong>Port of Discharge:</strong> {invoiceDialog.port_of_discharge}
                          </Typography>
                        </Grid>
                      )}
                    </Grid>
                  </Box>
                </Grid>

                {/* Invoice meta */}
                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom>
                    Invoice Information
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        label="Invoice Number"
                        value={invoiceDetails.invoiceNumber}
                        size="small"
                        disabled
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        label="Issue Date"
                        type="date"
                        value={invoiceDetails.issueDate}
                        onChange={(e) =>
                          setInvoiceDetails((p) => ({ ...p, issueDate: e.target.value }))
                        }
                        size="small"
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        label="Due Date"
                        type="date"
                        value={invoiceDetails.dueDate}
                        onChange={(e) =>
                          setInvoiceDetails((p) => ({ ...p, dueDate: e.target.value }))
                        }
                        size="small"
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        label="Payment Terms"
                        value={invoiceDetails.paymentTerms}
                        onChange={(e) =>
                          setInvoiceDetails((p) => ({ ...p, paymentTerms: e.target.value }))
                        }
                        size="small"
                      />
                    </Grid>
                  </Grid>
                </Grid>

                {/* Items table */}
                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom>
                    Invoice Items — All fields are editable
                  </Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: 'grey.100' }}>
                          <TableCell>Description</TableCell>
                          <TableCell align="center" sx={{ width: 80 }}>HSN Code</TableCell>
                          <TableCell align="center" sx={{ width: 90 }}>Grade</TableCell>
                          <TableCell align="center" sx={{ width: 80 }}>Qty (Cont.)</TableCell>
                          <TableCell align="right" sx={{ width: 130 }}>Unit Price (USD)</TableCell>
                          <TableCell align="right" sx={{ width: 110 }}>Amount</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {invoiceDetails.items.map((item, index) => (
                          <TableRow key={index}>
                            <TableCell>
                              <TextField
                                size="small"
                                value={item.description}
                                fullWidth
                                onChange={(e) =>
                                  setInvoiceDetails((prev) => ({
                                    ...prev,
                                    items: prev.items.map((it, i) =>
                                      i === index ? { ...it, description: e.target.value } : it
                                    ),
                                  }))
                                }
                              />
                            </TableCell>
                            <TableCell align="center">
                              <TextField
                                size="small"
                                value={item.hsn || ''}
                                placeholder="e.g. 0804"
                                onChange={(e) =>
                                  setInvoiceDetails((prev) => ({
                                    ...prev,
                                    items: prev.items.map((it, i) =>
                                      i === index ? { ...it, hsn: e.target.value } : it
                                    ),
                                  }))
                                }
                                sx={{ width: 80 }}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <TextField
                                size="small"
                                value={item.grade || ''}
                                placeholder="A / B"
                                onChange={(e) =>
                                  setInvoiceDetails((prev) => ({
                                    ...prev,
                                    items: prev.items.map((it, i) =>
                                      i === index ? { ...it, grade: e.target.value } : it
                                    ),
                                  }))
                                }
                                sx={{ width: 70 }}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <TextField
                                size="small"
                                type="number"
                                inputProps={{ min: 1, step: 1 }}
                                value={item.quantity || ''}
                                onChange={(e) => {
                                  const qty = parseInt(e.target.value) || 1;
                                  const amt = (item.unitPrice || 0) * qty;
                                  setInvoiceDetails((prev) => ({
                                    ...prev,
                                    items: prev.items.map((it, i) =>
                                      i === index ? { ...it, quantity: qty, amount: amt } : it
                                    ),
                                  }));
                                }}
                                sx={{ width: 65 }}
                              />
                            </TableCell>
                            <TableCell align="right">
                              <TextField
                                size="small"
                                type="number"
                                inputProps={{ min: 0, step: 0.01 }}
                                value={item.unitPrice || ''}
                                placeholder="0.00"
                                onChange={(e) => {
                                  const up  = parseFloat(e.target.value) || 0;
                                  const amt = up * (item.quantity || 1);
                                  setInvoiceDetails((prev) => ({
                                    ...prev,
                                    items: prev.items.map((it, i) =>
                                      i === index ? { ...it, unitPrice: up, amount: amt } : it
                                    ),
                                  }));
                                }}
                                sx={{ width: 110 }}
                              />
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                              {formatCurrency(item.amount)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <Alert severity="info" sx={{ mt: 1 }}>
                    <Typography variant="caption">
                      Edit description, HSN code, grade, quantity and unit price. Total updates automatically.
                    </Typography>
                  </Alert>
                </Grid>

                {/* Tax & discount */}
                <Grid item xs={12}>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        label="Tax Rate (%)"
                        type="number"
                        value={invoiceDetails.taxRate}
                        onChange={(e) =>
                          setInvoiceDetails((p) => ({
                            ...p,
                            taxRate: parseFloat(e.target.value) || 0,
                          }))
                        }
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        label="Discount (%)"
                        type="number"
                        value={invoiceDetails.discount}
                        onChange={(e) =>
                          setInvoiceDetails((p) => ({
                            ...p,
                            discount: parseFloat(e.target.value) || 0,
                          }))
                        }
                        size="small"
                      />
                    </Grid>
                  </Grid>
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Notes"
                    multiline
                    rows={3}
                    value={invoiceDetails.notes}
                    onChange={(e) =>
                      setInvoiceDetails((p) => ({ ...p, notes: e.target.value }))
                    }
                    size="small"
                  />
                </Grid>

                {/* Totals */}
                <Grid item xs={12}>
                  <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                    {[
                      {
                        label: 'Subtotal',
                        val: invoiceDetails.items.reduce((s, i) => s + i.amount, 0),
                      },
                      {
                        label: `Tax (${invoiceDetails.taxRate}%)`,
                        val:
                          (invoiceDetails.items.reduce((s, i) => s + i.amount, 0) *
                            invoiceDetails.taxRate) /
                          100,
                      },
                      {
                        label: `Discount (${invoiceDetails.discount}%)`,
                        val:
                          -(
                            (invoiceDetails.items.reduce((s, i) => s + i.amount, 0) *
                              invoiceDetails.discount) /
                            100
                          ),
                      },
                    ].map(({ label, val }) => (
                      <Box
                        key={label}
                        sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}
                      >
                        <Typography>{label}:</Typography>
                        <Typography>
                          {val < 0 ? '-' : ''}
                          {formatCurrency(Math.abs(val))}
                        </Typography>
                      </Box>
                    ))}
                    <Divider />
                    <Box
                      sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}
                    >
                      <Typography variant="h6">Total:</Typography>
                      <Typography variant="h6" color="primary">
                        {formatCurrency(calculateTotalAmount())}
                      </Typography>
                    </Box>
                  </Paper>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setInvoiceDialog(null);
              resetInvoiceDetails();
            }}
          >
            Cancel
          </Button>
          <Button
            variant="outlined"
            startIcon={<Print />}
            onClick={() => toast.success('Preview coming soon')}
          >
            Preview
          </Button>
          <Button variant="contained" startIcon={<Send />} onClick={handleSendInvoice}>
            Send Invoice
          </Button>
        </DialogActions>
      </Dialog>

      {/* ───────────────── Upload Invoice Dialog ───────────────── */}
      <Dialog
        open={Boolean(uploadInvoiceDialog)}
        onClose={() => {
          setUploadInvoiceDialog(null);
          setInvoiceFile(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CloudUpload />
            <Typography variant="h6">Upload Invoice</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {uploadInvoiceDialog && (
            <Box sx={{ mt: 2 }}>
              <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Enquiry Details
                </Typography>
                <Typography variant="body2">
                  <strong>Buyer:</strong> {uploadInvoiceDialog.buyerName || 'Unknown'}
                </Typography>
                <Typography variant="body2">
                  <strong>Product:</strong> {uploadInvoiceDialog.product || 'N/A'}
                </Typography>
                {uploadInvoiceDialog.selected_origin && (
                  <Typography variant="body2"><strong>Origin:</strong> {uploadInvoiceDialog.selected_origin}</Typography>
                )}
                {uploadInvoiceDialog.selected_variety && (
                  <Typography variant="body2"><strong>Variety:</strong> {uploadInvoiceDialog.selected_variety}</Typography>
                )}
                {uploadInvoiceDialog.selected_grade && (
                  <Typography variant="body2"><strong>Grade:</strong> {uploadInvoiceDialog.selected_grade}</Typography>
                )}
                {uploadInvoiceDialog.selected_size && (
                  <Typography variant="body2"><strong>Size:</strong> {uploadInvoiceDialog.selected_size}</Typography>
                )}
                {uploadInvoiceDialog.port_of_discharge && (
                  <Typography variant="body2"><strong>Port of Discharge:</strong> {uploadInvoiceDialog.port_of_discharge}</Typography>
                )}
                <Typography variant="body2">
                  <strong>Amount:</strong>{' '}
                  {formatCurrency(uploadInvoiceDialog.totalAmount || 0)}
                </Typography>
              </Box>
              <Box
                sx={{
                  border: '2px dashed #ccc',
                  borderRadius: 2,
                  p: 3,
                  textAlign: 'center',
                  cursor: 'pointer',
                  '&:hover': { borderColor: 'primary.main', bgcolor: 'primary.light' },
                }}
                onClick={() => document.getElementById('invoice-file-upload').click()}
              >
                <input
                  id="invoice-file-upload"
                  type="file"
                  accept=".pdf"
                  style={{ display: 'none' }}
                  onChange={handleFileUpload}
                />
                {invoiceFile ? (
                  <Box>
                    <DescriptionIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
                    <Typography variant="body1" gutterBottom>
                      {invoiceFile.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {(invoiceFile.size / 1024 / 1024).toFixed(2)} MB
                    </Typography>
                    <Box sx={{ mt: 2 }}>
                      <Button
                        variant="outlined"
                        startIcon={<DeleteIcon />}
                        onClick={(e) => {
                          e.stopPropagation();
                          setInvoiceFile(null);
                        }}
                        size="small"
                      >
                        Remove File
                      </Button>
                    </Box>
                  </Box>
                ) : (
                  <Box>
                    <CloudUpload sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                    <Typography variant="body1" gutterBottom>
                      Click to upload invoice PDF
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Max file size: 5MB
                    </Typography>
                  </Box>
                )}
              </Box>
              {/* Unit Price input for uploaded invoices */}
              <Box sx={{ mt: 2 }}>
                <TextField
                  fullWidth
                  label="Unit Price per Container (USD)"
                  type="number"
                  inputProps={{ min: 0, step: 0.01 }}
                  value={uploadUnitPrice}
                  onChange={(e) => setUploadUnitPrice(e.target.value)}
                  size="small"
                  required
                  helperText={
                    uploadUnitPrice && parseFloat(uploadUnitPrice) > 0 && uploadInvoiceDialog
                      ? `Total: $${(parseFloat(uploadUnitPrice) * (uploadInvoiceDialog.quantity || 1)).toFixed(2)} for ${uploadInvoiceDialog.quantity || 1} containers`
                      : 'Optional — enter to set the payment amount for the buyer on Pay Now page'
                  }
                />
              </Box>
              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="caption">
                  Upload a PDF invoice. The enquiry status will automatically update to
                  "Invoice Sent". The unit price sets the payment amount for the buyer.
                </Typography>
              </Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setUploadInvoiceDialog(null);
              setInvoiceFile(null);
              setUploadUnitPrice('');
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={<Upload />}
            onClick={handleUploadInvoice}
            disabled={!invoiceFile}
          >
            Upload Invoice
          </Button>
        </DialogActions>
      </Dialog>

      {/* ───────────────── View Invoice Dialog ───────────────── */}
      <Dialog
        open={Boolean(viewInvoiceDialog)}
        onClose={() => setViewInvoiceDialog(false)}
        maxWidth="lg"
        fullWidth
      >
        {selectedInvoice && (
          <>
            <DialogTitle>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Typography variant="h6">Invoice Details</Typography>
                <Chip
                  label={selectedInvoice.status}
                  color={getStatusColor(selectedInvoice.status)}
                />
              </Box>
            </DialogTitle>
            <DialogContent>
              <Box sx={{ mt: 2 }}>
                <Grid container spacing={3}>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" gutterBottom>
                      Invoice Information
                    </Typography>
                    <Paper sx={{ p: 2 }}>
                      <Typography variant="body2">
                        <strong>Invoice Number:</strong> {selectedInvoice.invoiceNumber}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Issue Date:</strong>{' '}
                        {formatDate(selectedInvoice.issueDate)}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Due Date:</strong> {formatDate(selectedInvoice.dueDate)}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Payment Terms:</strong> {selectedInvoice.paymentTerms}
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" gutterBottom>
                      Buyer Information
                    </Typography>
                    <Paper sx={{ p: 2 }}>
                      <Typography variant="body2" fontWeight="bold">
                        {selectedInvoice.buyerName}
                      </Typography>
                      <Typography variant="body2">{selectedInvoice.buyerEmail}</Typography>
                      <Typography variant="body2">{selectedInvoice.buyerPhone}</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" gutterBottom>
                      Invoice Items
                    </Typography>
                    <TableContainer component={Paper} variant="outlined">
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>Description</TableCell>
                            <TableCell align="right">Quantity</TableCell>
                            <TableCell align="right">Unit Price</TableCell>
                            <TableCell align="right">Amount</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {selectedInvoice.items?.map((item, index) => (
                            <TableRow key={index}>
                              <TableCell>{item.description}</TableCell>
                              <TableCell align="right">{item.quantity}</TableCell>
                              <TableCell align="right">
                                {formatCurrency(item.unitPrice)}
                              </TableCell>
                              <TableCell align="right">
                                {formatCurrency(item.amount)}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Grid>
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <Paper sx={{ p: 3, width: '50%' }}>
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            mb: 1,
                          }}
                        >
                          <Typography>Subtotal:</Typography>
                          <Typography>{formatCurrency(selectedInvoice.subtotal)}</Typography>
                        </Box>
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            mb: 1,
                          }}
                        >
                          <Typography>Tax ({selectedInvoice.taxRate}%):</Typography>
                          <Typography>
                            {formatCurrency(selectedInvoice.taxAmount)}
                          </Typography>
                        </Box>
                        {selectedInvoice.discount > 0 && (
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              mb: 1,
                            }}
                          >
                            <Typography>
                              Discount ({selectedInvoice.discount}%):
                            </Typography>
                            <Typography>
                              -{formatCurrency(selectedInvoice.discountAmount)}
                            </Typography>
                          </Box>
                        )}
                        <Divider sx={{ my: 2 }} />
                        <Box
                          sx={{ display: 'flex', justifyContent: 'space-between' }}
                        >
                          <Typography variant="h6">Total:</Typography>
                          <Typography variant="h6" color="primary">
                            {formatCurrency(selectedInvoice.totalAmount)}
                          </Typography>
                        </Box>
                      </Paper>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </DialogContent>
            <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
              <Button onClick={() => { setViewInvoiceDialog(false); setEditInvoiceMode(false); }}>Close</Button>
              {!editInvoiceMode ? (
                <>
                  <Button variant="outlined" startIcon={<Edit />}
                    onClick={() => {
                      setEditInvoiceData({
                        enquiryId:    selectedInvoice.enquiryId || viewInvoiceDialog,
                        invoiceNumber: selectedInvoice.invoiceNumber || '',
                        totalAmount:  selectedInvoice.totalAmount || 0,
                        unitPrice:    selectedInvoice.items?.[0]?.unitPrice || 0,
                        dueDate:      selectedInvoice.dueDate ? new Date(selectedInvoice.dueDate).toISOString().split('T')[0] : '',
                        paymentTerms: selectedInvoice.paymentTerms || 'Net 7 days',
                        notes:        selectedInvoice.notes || '',
                        status:       selectedInvoice.status || 'invoice_sent',
                      });
                      setEditInvoiceMode(true);
                    }}>
                    Edit Invoice
                  </Button>
                  <Button variant="outlined" startIcon={<Send />}
                    onClick={() => handleSendInvoiceToBuyer(selectedInvoice.invoiceNumber || selectedInvoice.invoiceId || selectedInvoice._id)}>
                    Resend to Buyer
                  </Button>
                  <Button variant="contained" color="primary" startIcon={<Download />}
                    onClick={() => handleDownloadInvoice(selectedInvoice._id, selectedInvoice.invoiceNumber)}>
                    Download PDF
                  </Button>
                </>
              ) : (
                <>
                  {/* Edit mode controls */}
                  <Grid container spacing={2} sx={{ px: 2, pb: 1, width: '100%' }}>
                    <Grid item xs={6} sm={3}>
                      <TextField fullWidth size="small" label="Invoice Number"
                        value={editInvoiceData.invoiceNumber || ''}
                        onChange={e => setEditInvoiceData(p => ({ ...p, invoiceNumber: e.target.value }))} />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <TextField fullWidth size="small" label="Total Amount (USD)" type="number"
                        value={editInvoiceData.totalAmount || ''}
                        onChange={e => setEditInvoiceData(p => ({ ...p, totalAmount: e.target.value }))} />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <TextField fullWidth size="small" label="Unit Price (USD)" type="number"
                        value={editInvoiceData.unitPrice || ''}
                        onChange={e => setEditInvoiceData(p => ({ ...p, unitPrice: e.target.value }))} />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <TextField fullWidth size="small" label="Due Date" type="date"
                        value={editInvoiceData.dueDate || ''} InputLabelProps={{ shrink: true }}
                        onChange={e => setEditInvoiceData(p => ({ ...p, dueDate: e.target.value }))} />
                    </Grid>
                    <Grid item xs={6} sm={4}>
                      <TextField fullWidth size="small" label="Payment Terms"
                        value={editInvoiceData.paymentTerms || ''}
                        onChange={e => setEditInvoiceData(p => ({ ...p, paymentTerms: e.target.value }))} />
                    </Grid>
                    <Grid item xs={12} sm={8}>
                      <TextField fullWidth size="small" label="Notes"
                        value={editInvoiceData.notes || ''}
                        onChange={e => setEditInvoiceData(p => ({ ...p, notes: e.target.value }))} />
                    </Grid>
                  </Grid>
                  <Button onClick={() => setEditInvoiceMode(false)}>Cancel Edit</Button>
                  <Button variant="contained" color="success" onClick={handleEditInvoiceSave}>
                    Save Changes
                  </Button>
                </>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>
      {/* ───────────────── Enquiry Detail Dialog (Bug 2 fix) ───────────────── */}
      <Dialog
        open={detailDialog}
        onClose={() => { setDetailDialog(false); setDetailEnquiry(null); if (urlId) navigate('/admin/enquiries'); }}
        maxWidth="md"
        fullWidth
      >
        {detailEnquiry && (
          <>
            <DialogTitle>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6">Enquiry #{detailEnquiry.enquiryId || detailEnquiry.id}</Typography>
                <Chip
                  label={detailEnquiry.status || 'pending'}
                  color={getStatusColor(detailEnquiry.status)}
                  size="small"
                  sx={{ textTransform: 'capitalize' }}
                />
              </Box>
            </DialogTitle>
            <DialogContent>
              <Grid container spacing={3} sx={{ mt: 0.5 }}>
                {/* Buyer Info */}
                <Grid item xs={12} md={6}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                      Buyer Information
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {[
                        ['Name', detailEnquiry.buyerName || detailEnquiry.user?.company_name || detailEnquiry.user?.email],
                        ['Email', detailEnquiry.buyerEmail || detailEnquiry.user?.email],
                        ['Phone', detailEnquiry.buyerPhone || detailEnquiry.user?.phone],
                        ['Country', detailEnquiry.country || detailEnquiry.user?.country],
                      ].map(([label, value]) => value ? (
                        <Box key={label} sx={{ display: 'flex', gap: 1 }}>
                          <Typography variant="body2" color="text.secondary" sx={{ minWidth: 70 }}>{label}:</Typography>
                          <Typography variant="body2" fontWeight="medium">{value}</Typography>
                        </Box>
                      ) : null)}
                    </Box>
                  </Paper>
                </Grid>
                {/* Product Info */}
                <Grid item xs={12} md={6}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                      Product & Specifications
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {[
                        ['Product', detailEnquiry.product || detailEnquiry.productName || detailEnquiry.fruit?.name],
                        ['Category', detailEnquiry.fruit?.category],
                        ['Quantity', detailEnquiry.quantity ? `${detailEnquiry.quantity} containers` : null],
                        ['Origin', detailEnquiry.selected_origin],
                        ['Variety', detailEnquiry.selected_variety],
                        ['Grade', detailEnquiry.selected_grade],
                        ['Size', detailEnquiry.selected_size],
                        ['Port of Discharge', detailEnquiry.port_of_discharge],
                      ].map(([label, value]) => value ? (
                        <Box key={label} sx={{ display: 'flex', gap: 1 }}>
                          <Typography variant="body2" color="text.secondary" sx={{ minWidth: 100 }}>{label}:</Typography>
                          <Typography variant="body2" fontWeight="medium">{value}</Typography>
                        </Box>
                      ) : null)}
                    </Box>
                  </Paper>
                </Grid>
                {/* Financial Info */}
                <Grid item xs={12} md={6}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                      Financial Details
                    </Typography>
                    {[
                      ['Total Amount', formatCurrency(detailEnquiry.totalAmount || 0)],
                      ['Unit Price', detailEnquiry.unitPrice ? formatCurrency(detailEnquiry.unitPrice) : '—'],
                      ['Invoice #', detailEnquiry.invoiceNumber || '—'],
                    ].map(([label, value]) => (
                      <Box key={label} sx={{ display: 'flex', gap: 1, mb: 0.5 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ minWidth: 100 }}>{label}:</Typography>
                        <Typography variant="body2" fontWeight="medium">{value}</Typography>
                      </Box>
                    ))}
                  </Paper>
                </Grid>
                {/* Timeline */}
                <Grid item xs={12} md={6}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                      Timeline
                    </Typography>
                    {[
                      ['Created', formatDate(detailEnquiry.createdAt || detailEnquiry.created_at)],
                      ['Updated', formatDate(detailEnquiry.updated_at)],
                      ['Responded', detailEnquiry.respondedAt ? formatDate(detailEnquiry.respondedAt) : '—'],
                    ].map(([label, value]) => (
                      <Box key={label} sx={{ display: 'flex', gap: 1, mb: 0.5 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ minWidth: 80 }}>{label}:</Typography>
                        <Typography variant="body2">{value}</Typography>
                      </Box>
                    ))}
                  </Paper>
                </Grid>
                {/* Message */}
                {detailEnquiry.message && (
                  <Grid item xs={12}>
                    <Paper variant="outlined" sx={{ p: 2 }}>
                      <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                        Buyer Message
                      </Typography>
                      <Typography variant="body2" color="text.secondary">{detailEnquiry.message}</Typography>
                    </Paper>
                  </Grid>
                )}
                {/* Admin Response */}
                {detailEnquiry.adminResponse && (
                  <Grid item xs={12}>
                    <Paper variant="outlined" sx={{ p: 2, bgcolor: 'success.light' }}>
                      <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                        Admin Response
                      </Typography>
                      <Typography variant="body2">{detailEnquiry.adminResponse}</Typography>
                    </Paper>
                  </Grid>
                )}

                {/* Supplier PO Instructions */}
                <Grid item xs={12}>
                  <Paper variant="outlined" sx={{ p: 2, border: detailEnquiry.seller_notes ? '1px solid #ed6c02' : undefined }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography variant="subtitle2" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Inventory fontSize="small" color="warning" />
                        Supplier / PO Instructions
                      </Typography>
                      <Button
                        size="small"
                        variant="outlined"
                        color="warning"
                        startIcon={<NoteAdd />}
                        onClick={() => { handleOpenPoDialog(detailEnquiry); setDetailDialog(false); }}
                      >
                        {detailEnquiry.seller_notes ? 'Edit PO Note' : 'Add PO Note'}
                      </Button>
                    </Box>
                    {detailEnquiry.seller_notes ? (
                      <Alert severity="warning" icon={false} sx={{ py: 0.5 }}>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                          {detailEnquiry.seller_notes}
                        </Typography>
                      </Alert>
                    ) : (
                      <Typography variant="body2" color="text.secondary" fontStyle="italic">
                        No PO instructions added yet. Click "Add PO Note" to send purchase order instructions to the supplier.
                      </Typography>
                    )}
                  </Paper>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => { setDetailDialog(false); setDetailEnquiry(null); if (urlId) navigate('/admin/enquiries'); }}>
                Close
              </Button>
              <Button
                variant="outlined"
                startIcon={<Edit />}
                onClick={() => { setStatusDialog(detailEnquiry); setDetailDialog(false); }}
              >
                Update Status
              </Button>
              {detailEnquiry.status !== 'cancelled' && (
                <Button
                  variant="contained"
                  startIcon={<Receipt />}
                  onClick={() => { handleCreateInvoice(detailEnquiry); setDetailDialog(false); }}
                >
                  {detailEnquiry.invoiceNumber ? 'Re-create Invoice' : 'Create Invoice'}
                </Button>
              )}
              {detailEnquiry.status !== 'cancelled' && (
                <Button
                  variant="outlined"
                  startIcon={<Upload />}
                  onClick={() => { handleOpenUploadInvoice(detailEnquiry); setDetailDialog(false); }}
                >
                  {detailEnquiry.invoiceNumber ? 'Re-upload Invoice' : 'Upload Invoice'}
                </Button>
              )}
              <Button
                variant="outlined"
                color="warning"
                startIcon={<NoteAdd />}
                onClick={() => { handleOpenPoDialog(detailEnquiry); setDetailDialog(false); }}
              >
                {detailEnquiry.seller_notes ? 'Edit PO Note' : 'Supplier PO'}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* ───────────────── Supplier PO Notes Dialog ───────────────── */}
      <Dialog open={Boolean(poDialog)} onClose={() => setPoDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <NoteAdd color="warning" />
            <Typography variant="h6">Supplier / Purchase Order Instructions</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {poDialog && (
            <Box sx={{ mt: 1 }}>
              <Alert severity="info" sx={{ mb: 2 }}>
                These instructions will be visible to the <strong>supplier</strong> on their Orders page. Use this to communicate packaging specs, delivery timelines, quality requirements, and any special instructions.
              </Alert>
              <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="body2">
                  <strong>Order:</strong> #{poDialog.enquiryId || poDialog._id || poDialog.id}
                </Typography>
                <Typography variant="body2">
                  <strong>Product:</strong> {poDialog.product || poDialog.productName || 'N/A'}
                </Typography>
                <Typography variant="body2">
                  <strong>Buyer:</strong> {poDialog.buyerName || 'Unknown'}
                </Typography>
                {poDialog.quantity && (
                  <Typography variant="body2">
                    <strong>Quantity:</strong> {poDialog.quantity} containers
                  </Typography>
                )}
              </Box>
              <TextField
                fullWidth
                multiline
                rows={6}
                label="PO Instructions for Supplier"
                placeholder={`e.g.\n- Pack in 10kg cartons\n- Attach phytosanitary certificate\n- Ship by [date]\n- Contact buyer: +1-XXX-XXX-XXXX\n- Grade A only, no blemishes`}
                value={poNotes}
                onChange={(e) => setPoNotes(e.target.value)}
                helperText={`${poNotes.length} characters`}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPoDialog(null)}>Cancel</Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleSavePoNotes}
            disabled={poSaving}
            startIcon={<NoteAdd />}
          >
            {poSaving ? 'Saving…' : 'Save & Notify Supplier'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ───────────────── Assign Seller Dialog ───────────────── */}
      <Dialog open={Boolean(sellerDialog)} onClose={() => setSellerDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Person color="primary" />
            Assign Seller to Enquiry
          </Box>
        </DialogTitle>
        <DialogContent>
          {sellerDialog && (
            <Box sx={{ mt: 1 }}>
              <Alert severity="info" sx={{ mb: 2 }}>
                Assign this buyer enquiry to a seller who will handle fulfillment.
                The seller will see it in their <strong>Assigned Enquiries</strong> dashboard.
              </Alert>
              <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="body2"><strong>Enquiry:</strong> #{sellerDialog.enquiryId || sellerDialog._id || sellerDialog.id}</Typography>
                <Typography variant="body2"><strong>Product:</strong> {sellerDialog.product || sellerDialog.productName || 'N/A'}</Typography>
                <Typography variant="body2"><strong>Buyer:</strong> {sellerDialog.buyerName || 'Unknown'}</Typography>
                {sellerDialog.assigned_seller_id && (
                  <Typography variant="body2" color="warning.main">
                    <strong>Currently assigned to seller ID:</strong> {sellerDialog.assigned_seller_id}
                  </Typography>
                )}
              </Box>
              <FormControl fullWidth>
                <InputLabel>Select Seller</InputLabel>
                <Select
                  value={selectedSeller}
                  label="Select Seller"
                  onChange={(e) => setSelectedSeller(e.target.value)}
                >
                  <MenuItem value=""><em>— No Assignment —</em></MenuItem>
                  {sellers.map(s => (
                    <MenuItem key={s.id} value={String(s.id)}>
                      {s.company_name || s.email}
                      {s.country && ` (${s.country})`}
                      {s.verified && ' ✅'}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {sellers.length === 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Loading sellers...
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSellerDialog(null)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleAssignSeller}
            disabled={sellerAssigning}
            startIcon={sellerAssigning ? <CircularProgress size={16} /> : <Person />}
          >
            {sellerAssigning ? 'Assigning...' : selectedSeller ? 'Assign Seller' : 'Remove Assignment'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ───────────────── Payment Terms per Enquiry Dialog ───────────────── */}
      <Dialog open={Boolean(ptDialog)} onClose={() => setPtDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Payment color="secondary" />
            Set Payment Terms — Enquiry #{ptDialog?._id || ptDialog?.id}
          </Box>
        </DialogTitle>
        <DialogContent>
          {ptDialog && (
            <Box sx={{ mt: 1 }}>
              <Alert severity="info" sx={{ mb: 2 }}>
                This overrides the buyer's account-level payment terms for <strong>this enquiry only</strong>.
                Leave as <em>Full Payment</em> to use the buyer's default terms.
              </Alert>
              <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="body2"><strong>Product:</strong> {ptDialog.product || ptDialog.productName || 'N/A'}</Typography>
                <Typography variant="body2"><strong>Buyer:</strong> {ptDialog.buyerName || 'Unknown'}</Typography>
                <Typography variant="body2" color="text.secondary">
                  Account-level terms: <strong>{ptDialog.buyer_payment_terms || 'full_100'}</strong>
                </Typography>
              </Box>
              <FormControl component="fieldset" fullWidth>
                <FormLabel component="legend" sx={{ mb: 1, fontWeight: 600 }}>
                  Select Payment Split for this Order
                </FormLabel>
                <RadioGroup value={ptTerms} onChange={(e) => setPtTerms(e.target.value)}>
                  {PAYMENT_TERMS_OPTIONS.map(opt => (
                    <FormControlLabel
                      key={opt.value}
                      value={opt.value}
                      control={<Radio />}
                      label={
                        <Box>
                          <Typography variant="body2" fontWeight="medium">{opt.label}</Typography>
                          <Typography variant="caption" color="text.secondary">{opt.desc}</Typography>
                        </Box>
                      }
                      sx={{
                        mb: 1, p: 1, borderRadius: 1, border: '1px solid',
                        borderColor: ptTerms === opt.value ? 'secondary.main' : 'divider',
                        bgcolor: ptTerms === opt.value ? 'secondary.50' : 'transparent',
                      }}
                    />
                  ))}
                </RadioGroup>
              </FormControl>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPtDialog(null)}>Cancel</Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={handleSavePtTerms}
            disabled={ptSaving}
            startIcon={ptSaving ? <CircularProgress size={16} /> : <Payment />}
          >
            {ptSaving ? 'Saving...' : 'Save Payment Terms'}
          </Button>
        </DialogActions>
      </Dialog>

    </Container>
  );
};

export default AdminEnquiries;